import {
  MessageType,
  useDownloadTaxReport,
  useGetFile,
  useWebSocketContext,
  useWebSocketDownloadTaxReport,
} from "@moonscape/shared";
import React, { useEffect, useState } from "react";
import { trackEvent } from "@/analytics";
import { CONVERSION_EVENTS, TAX_FEATURE_EVENTS } from "@/analytics/events";
import { Modal } from "@/components/ui/modal";
import { Tag } from "@/components/ui/tag";
import { Pill } from "@/components/ms";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";

interface DataType {
  key: string;
  name: string;
  detail: string;
  type: string;
}

const columns: DataTableColumn<DataType>[] = [
  {
    title: "Name",
    dataIndex: "name",
    key: "name",
    render: (text) => <a>{text}</a>,
  },
  {
    title: "Detail",
    dataIndex: "detail",
    key: "detail",
    render: (text) => <div>{text}</div>,
  },
  {
    title: "Type",
    key: "type",
    dataIndex: "type",
    render: (_, record) => (
      <Tag
        color={record.type?.toLocaleLowerCase() === "excel" ? "green" : "red"}
      >
        {record.type?.toUpperCase()}
      </Tag>
    ),
  },
];

export const TaxReport = ({ taxYear }) => {
  const [open, setOpen] = React.useState<boolean>(false);
  const {
    data,
    taxModes,
    sampleMode,
    downloadTaxReport,
    loading: isDownloadLoading,
    selectedMode,
    setSelectedMode,
  } = useDownloadTaxReport({});
  const { sendMessage } = useWebSocketContext();
  const { isQueryLoading: isDownloading, data: taxReportData } =
    useWebSocketDownloadTaxReport({ year: taxYear, mode: selectedMode });
  const { data: fileData, getFile, loading: isGettingFile } = useGetFile({});

  const showDownloadModal = () => {
    setOpen(true);
  };

  useEffect(() => {
    if (data) {
      downloadFile(data);
    }
  }, [data]);

  useEffect(() => {
    if (fileData) {
      downloadFile(fileData);

      // Track successful download
      trackEvent(TAX_FEATURE_EVENTS.TAX_REPORT_DOWNLOADED, {
        report_type: "tax_report",
        tax_year: taxYear,
        mode: selectedMode,
        source: "web",
      });
    }
  }, [fileData]);

  useEffect(() => {
    if (taxReportData?.fileId) {
      getFile({ fileId: taxReportData?.fileId });
    } else if (taxReportData?.error) {
      // Track tax report error
      trackEvent(TAX_FEATURE_EVENTS.TAX_REPORT_ERROR, {
        report_type: "tax_report",
        tax_year: taxYear,
        mode: selectedMode,
        error_type: "generation_error",
        error_message: taxReportData?.error || "Unknown error",
        source: "web",
      });
    }
  }, [taxReportData]);

  const downloadFile = (data) => {
    if (!data) {
      return;
    }
    const contentDisposition = data?.headers?.["content-disposition"];
    let filename;
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match?.[1]) {
        filename = match[1];
      }
    } else {
      return; // No filename found, exit early
    }

    // Create blob and trigger download
    const url = URL.createObjectURL(data?.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Pill onClick={showDownloadModal} disabled variant="off">
        Download report
      </Pill>
      <Modal
        title={<p className="text-foreground">Tax Reports In {taxYear}</p>}
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="xl"
        footer={
          <div className="flex items-center gap-3">
            {!selectedMode && (
              <p className="text-sm text-ms-neg mr-auto">Select form to download</p>
            )}
            <button
              onClick={() => downloadTaxReport({ taxYear, sampleMode: true })}
              disabled={isDownloadLoading && !!sampleMode}
              className="px-4 py-2 text-sm font-medium text-ms-link hover:text-ms-green transition-colors disabled:opacity-50"
            >
              {isDownloadLoading && !!sampleMode ? "Loading..." : "Get sample"}
            </button>
            <button
              onClick={() => {
                const startTime = Date.now();

                // Track conversion event (existing)
                trackEvent(CONVERSION_EVENTS.TAX_REPORT_GENERATED, {
                  tax_year: taxYear,
                  mode: selectedMode,
                  source: "web",
                });

                // Track Phase 3 event
                trackEvent(TAX_FEATURE_EVENTS.TAX_REPORT_GENERATED, {
                  report_type: "tax_report",
                  tax_year: taxYear,
                  mode: selectedMode,
                  source: "web",
                  timestamp_ms: startTime,
                });

                sendMessage({
                  type: MessageType.DOWNLOAD_TAX_REPORT,
                  payload: {
                    year: taxYear,
                    sampleMode: false,
                    mode: selectedMode,
                  },
                });
              }}
              disabled={isDownloading || isGettingFile}
              className="px-4 py-2 text-sm font-medium rounded-full bg-ms-pill-bg text-ms-pill-ink hover:bg-ms-pill-bg-hover transition-colors disabled:opacity-50"
            >
              {isDownloading || isGettingFile ? "Downloading..." : "Download"}
            </button>
          </div>
        }
      >
        <DataTable<DataType>
          columns={columns}
          dataSource={taxModes || []}
          rowKey="key"
          rowSelection={{
            type: "radio",
            selectedRowKeys: selectedMode ? [selectedMode] : [],
            onChange: (selectedKeys) => {
              setSelectedMode(selectedKeys[0] as string);
            },
          }}
        />
      </Modal>
    </>
  );
};
